/**
 * The locale vocabulary for every Ordo surface.
 *
 * Lives in `@ordo/constants` (zero runtime dependencies) rather than in
 * `@ordo/i18n`, because `@ordo/schema` and the pure title resolvers need to
 * name a locale without pulling in a FormatJS runtime. `@ordo/i18n` owns the
 * message catalogs and the `Intl` machinery; this file owns only the tags.
 *
 * Canonical form is BCP-47 with a HYPHEN. Four incompatible formats reach us
 * today and none of them are migrated — they are normalized at the boundary
 * by `toOrdoLocale`, which is the only place a raw string becomes a locale:
 *
 *   - `User.locale`                 underscores      `es_US`
 *   - `Invitation.locale`           IETF hyphens     `es-MX`
 *   - Multipass `AuthUser.locale`   bare subtag      `es`   (Google OIDC)
 *   - `navigator.language`          bare or regional `es`, `es-419`
 *
 * Negotiation falls back on the LANGUAGE SUBTAG, not exact match: `es`,
 * `es-MX` and `es_419` all resolve to `es-US`. Exact-match-or-English would
 * silently hand a Spanish speaker an English page, which is the failure this
 * whole system exists to prevent.
 */

export const ORDO_LOCALES = ['en-US', 'es-US'] as const

export type OrdoLocale = (typeof ORDO_LOCALES)[number]

export const DEFAULT_ORDO_LOCALE: OrdoLocale = 'en-US'

export function isOrdoLocale(value: unknown): value is OrdoLocale {
  return typeof value === 'string' && (ORDO_LOCALES as readonly string[]).includes(value)
}

/**
 * Normalize any inbound locale-ish string to a supported `OrdoLocale`.
 *
 * Never throws and never returns null — an unrecognized language yields
 * `en-US`. A page, a PDF or an email must still render for a user whose
 * stored locale is garbage.
 */
export function toOrdoLocale(raw: string | null | undefined): OrdoLocale {
  if (typeof raw !== 'string') {
    return DEFAULT_ORDO_LOCALE
  }
  // `es_US` / `es_419` → `es-US` / `es-419`, and strip any `.UTF-8`-style
  // POSIX charset suffix that a shell-sourced value can carry.
  const cleaned = raw.trim().replace(/_/g, '-').split('.')[0] ?? ''
  if (cleaned.length === 0) {
    return DEFAULT_ORDO_LOCALE
  }
  const [language = '', region = ''] = cleaned.split('-')
  const exact = `${language.toLowerCase()}-${region.toUpperCase()}`
  if (isOrdoLocale(exact)) {
    return exact
  }
  const bySubtag = ORDO_LOCALES.find((locale) => locale.split('-')[0] === language.toLowerCase())
  return bySubtag ?? DEFAULT_ORDO_LOCALE
}
